/**
 * HealthComponent - Manages entity health
 */

import { Component } from '../Component';
import { ComponentType } from '../../types/enums';

export class HealthComponent extends Component {
  readonly type = ComponentType.Health;

  private currentHealth: number;
  private maxHealth: number;
  private regenRate: number;
  private regenDelay: number;
  private timeSinceDamage: number = 0;
  private invulnerableTime: number = 0;

  public onDeath: (() => void) | null = null;
  public onDamage: ((amount: number) => void) | null = null;
  public onHeal: ((amount: number) => void) | null = null;

  constructor(maxHealth: number, regenRate: number = 0, regenDelay: number = 5) {
    super();
    this.maxHealth = maxHealth;
    this.currentHealth = maxHealth;
    this.regenRate = regenRate;
    this.regenDelay = regenDelay;
  }

  update(deltaTime: number): void {
    this.timeSinceDamage += deltaTime;

    // Invulnerability timer
    if (this.invulnerableTime > 0) {
      this.invulnerableTime -= deltaTime;
    }

    // Health regeneration
    if (this.regenRate > 0 && this.timeSinceDamage >= this.regenDelay && this.currentHealth < this.maxHealth) {
      this.heal(this.regenRate * deltaTime);
    }
  }

  /** Take damage */
  takeDamage(amount: number): number {
    if (this.isDead() || this.isInvulnerable()) return 0;

    const actualDamage = Math.min(amount, this.currentHealth);
    this.currentHealth -= actualDamage;
    this.timeSinceDamage = 0;

    this.onDamage?.(actualDamage);

    if (this.currentHealth <= 0) {
      this.currentHealth = 0;
      this.onDeath?.();
    }

    return actualDamage;
  }

  /** Heal health */
  heal(amount: number): number {
    if (this.isDead()) return 0;

    const actualHeal = Math.min(amount, this.maxHealth - this.currentHealth);
    this.currentHealth += actualHeal;

    if (actualHeal > 0) {
      this.onHeal?.(actualHeal);
    }

    return actualHeal;
  }

  /** Set invulnerable for duration */
  setInvulnerable(duration: number): void {
    this.invulnerableTime = Math.max(this.invulnerableTime, duration);
  }

  isInvulnerable(): boolean {
    return this.invulnerableTime > 0;
  }

  isDead(): boolean {
    return this.currentHealth <= 0;
  }

  getCurrentHealth(): number {
    return this.currentHealth;
  }

  getMaxHealth(): number {
    return this.maxHealth;
  }

  getHealthPercent(): number {
    return this.currentHealth / this.maxHealth;
  }

  setMaxHealth(value: number): void {
    this.maxHealth = value;
    this.currentHealth = Math.min(this.currentHealth, this.maxHealth);
  }

  setHealth(value: number): void {
    this.currentHealth = Math.max(0, Math.min(value, this.maxHealth));
  }

  setRegenRate(rate: number): void {
    this.regenRate = rate;
  }
}
